/**
 * Administração › Dispositivos: lista, novo e detalhe dos CLP/RTU de campo — Optimale Grid (frontend, US-2001).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.1.0
 *
 * Cadastro estrutural da Optimale (`editar_estrutura`): filial, município e setor vêm das listas cadastradas (Sprint 19).
 * O `device_id` não muda; realocar exige motivo. O detalhe é aberto também pela Segurança (quem vê a operadora lê;
 * revogar continua na Segurança). O ativo da rede se liga pelo campo "Dispositivo de telemetria" do ativo.
 * US-3011: ritmo de envio e tempo sem sinal para alarmar (vazio = 15 min), com o ritmo medido no hub como sugestão.
 * v2.0 (US-4604, Sprint 46): lista com migalhas, KPIs que filtram (total, ativos, desativados, revogados, certificado
 * vencendo), busca com `/`, filtros no endereço com pílulas e painel de detalhe ao lado; novo em grupos com validação ao
 * sair do campo e rodapé fixo; detalhe com ficha, situação, certificados e históricos em linha do tempo. Mesmas chamadas
 * e permissões de antes.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { Certificados } from "./Certificados";
import { quando } from "./tipos";
import { marcaAtual } from "../marca";
import {
  avisar, BarraFiltros, CabecalhoTela, Campo, EstadoVazio, ErroCarregar, Esqueleto, FaixaKpis, GrupoCampos, ListaDetalhe, RodapeFormulario,
  Segmentado, Selo, TabelaDensa, useAvisoAoSair, useLembrarEndereco, useParametro, type Coluna,
} from "../componentes/Tela";
import { casaBusca, ordenar, type Ordem } from "../componentes/regrasTela";
import "./estrutura.css";

type Dispositivo = { device_id: string; empresa: string; filial: string; municipio: string; unidade: string; tipo: string;
  observacao: string; situacao: "ativo" | "desativado" | "revogado"; versao: number; criado_por: string; criado_em: string | null;
  topico: string; revogado_em: string | null; revogado_por: string | null; motivo_revogacao: string | null;
  desativado_em: string | null; desativado_por: string | null; motivo_desativacao: string | null;
  certificado_valido_ate?: string | null; certificado_vence_em_breve?: boolean;
  intervalo_envio_min: number | null; alarme_sem_sinal_min: number | null; alarme_sem_sinal_efetivo_min: number };
type RitmoMedido = { mediana_min: number; p95_min: number; intervalos: number; alarme_sugerido_min: number };
/** US-3011: os dois tempos do cadastro, em texto no formulário (vazio = não informado). */
type Ritmo = { intervalo_envio_min: string; alarme_sem_sinal_min: string };
const minutos = (v: string): number | null => (v.trim() ? Number(v) : null);
const ritmoCorpo = (r: Ritmo) => ({ intervalo_envio_min: minutos(r.intervalo_envio_min), alarme_sem_sinal_min: minutos(r.alarme_sem_sinal_min) });
const duracao = (m: number) => (m >= 120 ? `${Math.round(m / 6) / 10} h` : `${m} min`);
const CODIGO = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** Minutos entre 1 e 10080 (7 dias), ou vazio — os mesmos limites do campo. */
const erroMinutos = (v: string): string | null => {
  if (!v.trim()) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 10080 ? null : "Minutos inteiros de 1 a 10080 (7 dias).";
};
const errosRitmo = (r: Ritmo) => ({ intervalo_envio_min: erroMinutos(r.intervalo_envio_min), alarme_sem_sinal_min: erroMinutos(r.alarme_sem_sinal_min) });

/** Campos do ritmo de envio e do tempo sem sinal (novo e edição). */
function CamposRitmo({ valor, aoMudar, medido }: { valor: Ritmo; aoMudar: (v: Ritmo) => void; medido?: RitmoMedido | null }) {
  const [tocados, setTocados] = useState<Set<keyof Ritmo>>(new Set());
  const erros = errosRitmo(valor);
  const erro = (k: keyof Ritmo) => (tocados.has(k) ? erros[k] : null);
  const tocar = (k: keyof Ritmo) => setTocados((t) => new Set(t).add(k));
  return <>
    <Campo id="disp-intervalo" rotulo="Envia a cada (min)" erro={erro("intervalo_envio_min")} ajuda="Vazio: não informado">
      <input id="disp-intervalo" type="number" min={1} max={10080} value={valor.intervalo_envio_min} placeholder="não informado" onBlur={() => tocar("intervalo_envio_min")}
        onChange={(e) => aoMudar({ ...valor, intervalo_envio_min: e.target.value })} />
    </Campo>
    <Campo id="disp-sem-sinal" rotulo="Alarmar sem sinal após (min)" erro={erro("alarme_sem_sinal_min")} ajuda="Vazio: 60 min (padrão)">
      <input id="disp-sem-sinal" type="number" min={1} max={10080} value={valor.alarme_sem_sinal_min} placeholder="15 (padrão)" onBlur={() => tocar("alarme_sem_sinal_min")}
        onChange={(e) => aoMudar({ ...valor, alarme_sem_sinal_min: e.target.value })} />
    </Campo>
    {medido && <p className="nx-suave tl-campo largo" style={{ margin: 0 }}>Medido nos últimos 7 dias: chega a cada {duracao(medido.mediana_min)} (mediana de {medido.intervalos} intervalos).{" "}
      <button type="button" className="nx-botao botao-linha" onClick={() => aoMudar({ intervalo_envio_min: String(Math.max(1, Math.round(medido.mediana_min))),
        alarme_sem_sinal_min: String(medido.alarme_sugerido_min) })}>Usar o medido ({duracao(medido.alarme_sugerido_min)} para alarmar)</button></p>}
  </>;
}
type Detalhe = Dispositivo & { ritmo_medido: RitmoMedido | null; ativos: { id: string; identificacao: string; tipo: string; situacao: string }[];
  eventos_seguranca: { instante: string; tipo: string; severidade: string; motivo: string | null }[];
  historico: { instante: string; autor: string; evento: string; detalhe: Record<string, unknown> }[];
  pode_editar: boolean; pode_revogar: boolean };
type Estrutura = { filiais: string[]; municipios: { codigo: string; nome: string }[]; setores: { municipio: string; codigo: string; nome: string }[] };

export const SITUACAO_DISPOSITIVO: Record<Dispositivo["situacao"], [string, string]> = {
  ativo: ["Ativo", "situacao-validado"], desativado: ["Desativado", "situacao-pendente"], revogado: ["Revogado", "situacao-rejeitado"] };
const TOM_SITUACAO: Record<Dispositivo["situacao"], "ok" | "atencao" | "ruim"> = { ativo: "ok", desativado: "atencao", revogado: "ruim" };
const TIPOS = ["bomba", "reservatorio", "poco", "ponto_pressao", "macromedidor", "vrp", "clp", "rtu"];
const EVENTO: Record<string, string> = { dispositivo_criado: "Cadastrado", dispositivo_alterado: "Dados alterados",
  dispositivo_realocado: "Realocado", dispositivo_desativado: "Desativado", dispositivo_reativado: "Reativado" };
const MIGALHA_ADMIN = { texto: NOMES.administracao };
const SEM_ACESSO = () => <p className="nx-suave">Cadastro estrutural: só a equipe {marcaAtual().empresa}.</p>;

/** Filiais, municípios e setores ativos cadastrados da empresa (para as listas de escolha). */
function useEstrutura(empresa: string): Estrutura {
  const [e, setE] = useState<Estrutura>({ filiais: [], municipios: [], setores: [] });
  useEffect(() => {
    if (!empresa) return;
    Promise.all([
      api<{ filiais: { codigo: string; ativa: boolean }[] }>(`/v1/admin/empresas/${empresa}/filiais`),
      api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`),
      api<{ setores: { municipio: string; codigo: string; nome: string; ativa: boolean }[] }>(`/v1/admin/empresas/${empresa}/setores`),
    ]).then(([f, m, s]) => setE({ filiais: f.filiais.filter((x) => x.ativa).map((x) => x.codigo),
      municipios: m.municipios.filter((x) => x.cadastrado), setores: s.setores.filter((x) => x.ativa) }))
      .catch(() => setE({ filiais: [], municipios: [], setores: [] }));
  }, [empresa]);
  return e;
}

type Loc = { filial: string; municipio: string; unidade: string };
function CamposLocalizacao({ estrutura, valor, aoMudar }: { estrutura: Estrutura; valor: Loc; aoMudar: (v: Loc) => void }) {
  const [tocados, setTocados] = useState<Set<keyof Loc>>(new Set());
  const tocar = (k: keyof Loc) => setTocados((t) => new Set(t).add(k));
  const erro = (k: keyof Loc, texto: string) => (tocados.has(k) && !valor[k] ? texto : null);
  return (
    <>
      <Campo id="disp-filial" rotulo="Filial" erro={erro("filial", "Escolha a filial (cadastre-a na empresa).")}>
        <select id="disp-filial" required value={valor.filial} onBlur={() => tocar("filial")} onChange={(e) => aoMudar({ ...valor, filial: e.target.value })}>
          <option value="">—</option>{estrutura.filiais.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </Campo>
      <Campo id="disp-municipio" rotulo="Município" erro={erro("municipio", "Escolha o município.")}>
        <select id="disp-municipio" required value={valor.municipio} onBlur={() => tocar("municipio")} onChange={(e) => aoMudar({ ...valor, municipio: e.target.value, unidade: "" })}>
          <option value="">—</option>{estrutura.municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
        </select>
      </Campo>
      <Campo id="disp-unidade" rotulo="Setor / unidade" erro={erro("unidade", "Escolha o setor do município.")}>
        <select id="disp-unidade" required value={valor.unidade} onBlur={() => tocar("unidade")} onChange={(e) => aoMudar({ ...valor, unidade: e.target.value })}>
          <option value="">—</option>
          {estrutura.setores.filter((s) => s.municipio === valor.municipio).map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo} · {s.nome}</option>)}
        </select>
      </Campo>
    </>
  );
}

/** Certificado na lista e no painel: data de validade, com aviso quando vence em breve. */
function TextoCertificado({ d }: { d: Dispositivo }) {
  if (!d.certificado_valido_ate) return <span className="nx-suave">—</span>;
  return <span className={d.certificado_vence_em_breve ? "texto-certificado-vence" : ""}>{d.certificado_vence_em_breve ? "▲ " : ""}até {quando(d.certificado_valido_ate)}
    {d.certificado_vence_em_breve ? " · vence em breve" : ""}</span>;
}

export function Dispositivos() {
  const { usuario, empresa: empresaSessao } = useSessao();
  const navegar = useNavigate();
  useLembrarEndereco();
  const [parametros] = useSearchParams();
  const [, setEmpresaParam] = useParametro("empresa");
  const empresa = parametros.get("empresa") ?? empresaSessao ?? "";
  const [situacao, setSituacao] = useParametro("situacao");
  const [busca, setBusca] = useParametro("busca");
  const [kpi, setKpi] = useParametro("kpi");
  const [sel, setSel] = useParametro("sel");
  const [ordem, setOrdem] = useState<Ordem>({ coluna: "id", crescente: true });
  const [painelAberto, setPainelAberto] = useState(false);
  const [lista, setLista] = useState<Dispositivo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    setErro(null);
    const q = new URLSearchParams({ ...(empresa ? { empresa } : {}), ...(situacao ? { situacao } : {}) });
    api<Dispositivo[]>(`/v1/admin/dispositivos?${q}`).then(setLista).catch((e) => setErro(String(e.message ?? e)));
  }, [empresa, situacao, tentativa]);
  const contagem = useMemo(() => {
    const c = { total: 0, ativo: 0, desativado: 0, revogado: 0, vence: 0, semCert: 0, padrao: 0 };
    for (const d of lista ?? []) {
      c.total++; c[d.situacao]++;
      if (d.certificado_vence_em_breve) c.vence++;
      if (!d.certificado_valido_ate) c.semCert++;
      if (!d.alarme_sem_sinal_min) c.padrao++;
    }
    return c;
  }, [lista]);
  const visiveis = useMemo(() => {
    const filtradas = (lista ?? []).filter((d) => (kpi !== "vence" || d.certificado_vence_em_breve) && (kpi !== "sem_certificado" || !d.certificado_valido_ate)
      && casaBusca(busca, d.device_id, d.empresa, d.filial, d.municipio, d.unidade, d.tipo, d.observacao));
    const valor = (d: Dispositivo) => ordem.coluna === "empresa" ? `${d.empresa} ${d.device_id}` : ordem.coluna === "local" ? `${d.filial}/${d.municipio}/${d.unidade}`
      : ordem.coluna === "tipo" ? d.tipo : ordem.coluna === "sinal" ? d.alarme_sem_sinal_efetivo_min : ordem.coluna === "situacao" ? d.situacao
      : ordem.coluna === "certificado" ? d.certificado_valido_ate ?? null : d.device_id;
    return ordenar(filtradas, valor, ordem.crescente);
  }, [lista, kpi, busca, ordem]);
  if (!usuario || !pode.verEstrutura(usuario)) return <SEM_ACESSO />;
  const selecionado = visiveis.find((d) => d.device_id === sel) ?? visiveis[0] ?? null;
  const edita = pode.editarEstrutura(usuario);
  // KPIs: os de situação usam o mesmo filtro `situacao` da API; os de certificado filtram na tela
  const kpiAtivo = situacao || kpi;
  const escolherKpi = (chave: string) => {
    if (chave === "vence" || chave === "sem_certificado") { setKpi(chave); return; }
    setKpi(""); setSituacao(chave);
  };
  const pilulas = [
    ...(empresa && parametros.get("empresa") ? [{ chave: "empresa", texto: `Operadora ${empresa}` }] : []),
    ...(situacao ? [{ chave: "situacao", texto: SITUACAO_DISPOSITIVO[situacao as Dispositivo["situacao"]]?.[0] ?? situacao }] : []),
    ...(kpi ? [{ chave: "kpi", texto: kpi === "vence" ? "Certificado vence em breve" : "Sem certificado" }] : []),
    ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (chave: string) => ({ empresa: setEmpresaParam, situacao: setSituacao, kpi: setKpi, busca: setBusca } as Record<string, (v: string) => void>)[chave]?.("");
  const limpar = () => { setSituacao(""); setKpi(""); setBusca(""); };
  const colunas: Coluna<Dispositivo>[] = [
    { chave: "id", titulo: "Dispositivo", ordenavel: true, celula: (d) => (
      <div className="tl-ident"><div style={{ minWidth: 0 }}><b>{d.device_id}</b><span>{d.tipo || "tipo não informado"}</span></div></div>) },
    { chave: "empresa", titulo: "Operadora", ordenavel: true, celula: (d) => <span className="tl-mono">{d.empresa}</span> },
    { chave: "local", titulo: "Filial · município · setor", ordenavel: true, celula: (d) => <span className="tl-mono">{d.filial} · {d.municipio} · {d.unidade}</span> },
    { chave: "sinal", titulo: "Sem sinal", ordenavel: true, celula: (d) => <>{duracao(d.alarme_sem_sinal_efetivo_min)}{d.alarme_sem_sinal_min ? "" : <small className="nx-suave"> (padrão)</small>}</> },
    { chave: "situacao", titulo: "Situação", ordenavel: true, celula: (d) => <Selo tom={TOM_SITUACAO[d.situacao]}>{SITUACAO_DISPOSITIVO[d.situacao][0]}</Selo> },
    { chave: "certificado", titulo: "Certificado", ordenavel: true, celula: (d) => <TextoCertificado d={d} /> },
  ];
  return (
    <section className="tl-pagina admin-estrutura">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[MIGALHA_ADMIN, { texto: NOMES.dispositivos }]} titulo={NOMES.dispositivos}
        subtitulo={empresa ? `CLP e RTU de campo · ${empresa}` : "CLP e RTU de campo · todas as operadoras"}
        acoes={edita ? <Link className="nx-botao nx-botao-principal" to={`/admin/dispositivos/novo${empresa ? `?empresa=${empresa}` : ""}`}>Novo dispositivo</Link> : undefined} />
      {erro ? <ErroCarregar mensagem={`Não foi possível carregar os dispositivos: ${erro}`} aoTentar={() => setTentativa((t) => t + 1)} /> : !lista ? <Esqueleto linhas={8} /> : (
        <>
          <FaixaKpis ativo={kpiAtivo} aoEscolher={escolherKpi} itens={[
            { chave: "", rotulo: "Dispositivos", valor: contagem.total, detalhe: `${contagem.padrao} com tempo sem sinal padrão` },
            { chave: "ativo", rotulo: "Ativos", valor: contagem.ativo, detalhe: "a ingestão aceita" },
            { chave: "desativado", rotulo: "Desativados", valor: contagem.desativado, detalhe: "a ingestão recusa" },
            { chave: "revogado", rotulo: "Revogados", valor: contagem.revogado, detalhe: "comprometidos (Segurança)", tom: contagem.revogado ? "critico" : undefined },
            { chave: "vence", rotulo: "Certificado vencendo", valor: contagem.vence, detalhe: "▲ renove antes de vencer", tom: contagem.vence ? "alarme" : undefined },
            { chave: "sem_certificado", rotulo: "Sem certificado", valor: contagem.semCert, detalhe: "emita no detalhe" },
          ]} />
          <BarraFiltros busca={busca} aoBuscar={setBusca} placeholder="Buscar por dispositivo, operadora, filial, município, setor ou tipo"
            pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
            <label htmlFor="disp-f-empresa">Operadora</label>
            <input id="disp-f-empresa" value={empresa} onChange={(e) => setEmpresaParam(e.target.value.trim())} placeholder="todas" />
            <Segmentado rotulo="Situação" valor={situacao} aoMudar={(v) => { setKpi(""); setSituacao(v); }}
              opcoes={[{ valor: "", texto: "Todas" }, ...Object.entries(SITUACAO_DISPOSITIVO).map(([v, [t]]) => ({ valor: v, texto: t }))]} />
          </BarraFiltros>
          <ListaDetalhe aberto={painelAberto}
            lista={(
              <TabelaDensa rotulo={NOMES.dispositivos} colunas={colunas} linhas={visiveis} chave={(d) => d.device_id} selecionada={selecionado?.device_id ?? null}
                aoSelecionar={(d) => { setSel(d.device_id); setPainelAberto(true); }} aoAbrir={(d) => navegar(`/admin/dispositivos/${d.device_id}`)}
                ordem={ordem} aoOrdenar={setOrdem}
                cabecalho={<span><b>{visiveis.length}</b> de {lista.length} dispositivos</span>}
                vazio={<EstadoVazio titulo="Nenhum dispositivo com esses filtros" texto="Tire um filtro ou cadastre o CLP que falta."
                  acao={pilulas.length ? <button type="button" className="nx-botao" onClick={() => { limpar(); setEmpresaParam(""); }}>Limpar filtros</button>
                    : edita ? <Link className="nx-botao" to="/admin/dispositivos/novo">Cadastrar dispositivo</Link> : undefined} />}
                cartao={(d) => (
                  <>
                    <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}>
                      <b className="tl-mono">{d.device_id}</b><Selo tom={TOM_SITUACAO[d.situacao]}>{SITUACAO_DISPOSITIVO[d.situacao][0]}</Selo></div>
                    <span className="tl-mono nx-suave">{d.empresa} · {d.filial} · {d.municipio} · {d.unidade}</span>
                    <span style={{ fontSize: 13 }}>Sem sinal após {duracao(d.alarme_sem_sinal_efetivo_min)} · <TextoCertificado d={d} /></span>
                  </>
                )} />
            )}
            detalhe={selecionado ? (
              <div className="tl-det-corpo">
                <div className="tl-det-titulo"><div style={{ flex: 1, minWidth: 0 }}><h2 className="tl-mono">{selecionado.device_id}</h2><p>{selecionado.tipo || "tipo não informado"}</p></div>
                  <button type="button" className="tl-fantasma tl-fechar" onClick={() => setPainelAberto(false)} aria-label="Fechar o detalhe">×</button></div>
                <div className="tl-linha-acoes"><Selo tom={TOM_SITUACAO[selecionado.situacao]}>{SITUACAO_DISPOSITIVO[selecionado.situacao][0]}</Selo></div>
                <dl className="tl-kv">
                  <dt>Operadora</dt><dd>{selecionado.empresa}</dd>
                  <dt>Filial</dt><dd>{selecionado.filial}</dd><dt>Município</dt><dd>{selecionado.municipio}</dd><dt>Setor</dt><dd>{selecionado.unidade}</dd>
                  <dt>Observação</dt><dd>{selecionado.observacao || "—"}</dd>
                  <dt>Envia a cada</dt><dd>{selecionado.intervalo_envio_min ? duracao(selecionado.intervalo_envio_min) : "não informado"}</dd>
                  <dt>Sem sinal após</dt><dd>{duracao(selecionado.alarme_sem_sinal_efetivo_min)}{selecionado.alarme_sem_sinal_min ? "" : " (padrão)"}</dd>
                  <dt>Certificado</dt><dd><TextoCertificado d={selecionado} /></dd>
                  {selecionado.desativado_em && <><dt>Desativado</dt><dd>{quando(selecionado.desativado_em)}: {selecionado.motivo_desativacao}</dd></>}
                  {selecionado.revogado_em && <><dt>Revogado</dt><dd>{quando(selecionado.revogado_em)}: {selecionado.motivo_revogacao}</dd></>}
                  <dt>Cadastrado</dt><dd>{selecionado.criado_em ? quando(selecionado.criado_em) : "—"}</dd>
                </dl>
                <div className="tl-linha-acoes">
                  <Link className="nx-botao nx-botao-principal" to={`/admin/dispositivos/${selecionado.device_id}`}>Abrir dispositivo</Link>
                  <Link className="nx-botao" to={`/cco/equipamento/${encodeURIComponent(selecionado.device_id)}`}>Ver no CCO</Link>
                </div>
              </div>
            ) : undefined} />
        </>
      )}
    </section>
  );
}

export function DispositivoNovo() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const [form, setForm] = useState({ device_id: "", empresa: parametros.get("empresa") ?? "", tipo: "", observacao: "" });
  const [ritmo, setRitmo] = useState<Ritmo>({ intervalo_envio_min: "", alarme_sem_sinal_min: "" });
  const [loc, setLoc] = useState<Loc>({ filial: "", municipio: "", unidade: "" });
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const estrutura = useEstrutura(form.empresa);
  const sujo = !!(form.device_id || form.tipo || form.observacao || loc.filial || ritmo.intervalo_envio_min || ritmo.alarme_sem_sinal_min);
  useAvisoAoSair(sujo && !enviando);
  if (!usuario || !pode.editarEstrutura(usuario)) return <SEM_ACESSO />;
  const erroId = !CODIGO.test(form.device_id) ? "Letras minúsculas, números, - ou _ (ex.: clp-eta-01). É o CN do certificado e não muda." : null;
  const erroEmpresa = !form.empresa ? "Informe a operadora (ex.: empresa01)." : null;
  const r = errosRitmo(ritmo);
  const valido = !erroId && !erroEmpresa && !!loc.filial && !!loc.municipio && !!loc.unidade && !r.intervalo_envio_min && !r.alarme_sem_sinal_min;
  const tocar = (k: string) => setTocados((t) => new Set(t).add(k));
  const criar = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valido) { setTocados(new Set(["device_id", "empresa"])); return; }
    setErro(null); setEnviando(true);
    try {
      const d = await api<Dispositivo>("/v1/admin/dispositivos", { corpo: { ...form, ...loc, ...ritmoCorpo(ritmo) } });
      avisar(`Dispositivo ${d.device_id} cadastrado.`);
      navegar(`/admin/dispositivos/${d.device_id}`);
    } catch (x) { setErro(x instanceof ErroApi ? x.message : "Falha de rede."); setEnviando(false); }
  };
  return (
    <section className="tl-pagina admin-estrutura">
      <CabecalhoTela voltar="/admin/dispositivos" migalhas={[MIGALHA_ADMIN, { texto: NOMES.dispositivos, para: "/admin/dispositivos" }, { texto: "Novo dispositivo" }]}
        titulo="Novo dispositivo" subtitulo="CLP ou RTU de campo" />
      <form className="tl-bloco" onSubmit={(e) => void criar(e)} aria-label="Novo dispositivo">
        <div className="tl-painel-aba">
          <GrupoCampos titulo="Identificação">
            <Campo id="disp-id" rotulo="Identificador (CN do certificado)" erro={tocados.has("device_id") ? erroId : null}>
              <input id="disp-id" required pattern="[a-z0-9][a-z0-9_\-]{0,63}" value={form.device_id} onBlur={() => tocar("device_id")}
                onChange={(e) => setForm({ ...form, device_id: e.target.value.trim().toLowerCase() })} placeholder="ex.: clp-eta-01" />
            </Campo>
            <Campo id="disp-empresa" rotulo="Operadora" erro={tocados.has("empresa") ? erroEmpresa : null} ajuda="As listas de filial, município e setor vêm dela">
              <input id="disp-empresa" required value={form.empresa} onBlur={() => tocar("empresa")}
                onChange={(e) => { setForm({ ...form, empresa: e.target.value.trim() }); setLoc({ filial: "", municipio: "", unidade: "" }); }} />
            </Campo>
            <Campo id="disp-tipo" rotulo="Tipo de equipamento">
              <input id="disp-tipo" list="tipos-dispositivo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value.trim().toLowerCase() })} />
            </Campo>
            <datalist id="tipos-dispositivo">{TIPOS.map((t) => <option key={t} value={t} />)}</datalist>
            <Campo id="disp-obs" rotulo="Observação">
              <input id="disp-obs" maxLength={300} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
            </Campo>
          </GrupoCampos>
          <GrupoCampos titulo="Localização (tópico MQTT)">
            <CamposLocalizacao estrutura={estrutura} valor={loc} aoMudar={setLoc} />
          </GrupoCampos>
          <GrupoCampos titulo="Comunicação">
            <CamposRitmo valor={ritmo} aoMudar={setRitmo} />
          </GrupoCampos>
          {erro && <ErroCarregar mensagem={erro} />}
        </div>
        <RodapeFormulario sujo={sujo} valido={valido} salvando={enviando} aoCancelar={() => navegar("/admin/dispositivos")} aoSalvar={() => void criar()}
          rotuloSalvar="Cadastrar dispositivo" />
      </form>
    </section>
  );
}

export function DispositivoDetalhe() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [d, setD] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<({ tipo: string; observacao: string } & Ritmo) | null>(null);
  const [realocacao, setRealocacao] = useState<(Loc & { motivo: string }) | null>(null);
  const [motivoRealocTocado, setMotivoRealocTocado] = useState(false);
  const [motivo, setMotivo] = useState("");
  const carregar = useCallback(() => {
    setErro(null);
    api<Detalhe>(`/v1/admin/dispositivos/${id}`).then(setD)
      .catch((x) => setErro(x instanceof ErroApi && x.status === 404 ? "Dispositivo não encontrado." : String(x.message ?? x)));
  }, [id]);
  useEffect(carregar, [carregar, usuario]);
  const estrutura = useEstrutura(d?.pode_editar && usuario && pode.editarEstrutura(usuario) && pode.verEmpresa(usuario, d.empresa) ? d.empresa : "");
  useAvisoAoSair(!!edicao || !!realocacao);
  if (!usuario) return null;
  const daEstrutura = pode.verEstrutura(usuario);
  const voltar = daEstrutura ? "/admin/dispositivos" : "/cco/seguranca";
  const migalhas = daEstrutura ? [MIGALHA_ADMIN, { texto: NOMES.dispositivos, para: "/admin/dispositivos" }, { texto: id ?? "" }]
    : [{ texto: "CCO" }, { texto: "Segurança", para: "/cco/seguranca" }, { texto: id ?? "" }];
  if (erro) return <section className="tl-pagina admin-estrutura"><CabecalhoTela voltar={voltar} migalhas={migalhas} titulo="Dispositivo" />
    <ErroCarregar mensagem={erro} aoTentar={carregar} /></section>;
  if (!d || d.device_id !== id) return <section className="tl-pagina admin-estrutura"><Esqueleto linhas={10} /></section>;
  if (!pode.verEmpresa(usuario, d.empresa)) return <p className="nx-suave">Dispositivo não encontrado.</p>;
  const edita = d.pode_editar && pode.editarEstrutura(usuario);
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(null); avisar(ok); setEdicao(null); setRealocacao(null); setMotivo(""); carregar(); }
    catch (x) {
      if (x instanceof ErroApi && x.status === 409 && /versão/.test(x.message)) { setAviso("Outra pessoa alterou este dispositivo antes. Os dados foram recarregados."); carregar(); }
      else setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede.");
    }
  };
  const base = `/v1/admin/dispositivos/${d.device_id}`;
  const [rotulo, classe] = SITUACAO_DISPOSITIVO[d.situacao];
  const salvarEdicao = () => {
    if (!edicao) return;
    void executar(() => api(base, { metodo: "PUT", corpo: { versao: d.versao, tipo: edicao.tipo, observacao: edicao.observacao, ...ritmoCorpo(edicao) } }), "Alterações salvas.");
  };
  const errosEdicao = edicao ? errosRitmo(edicao) : null;
  const edicaoSuja = !!edicao && (edicao.tipo !== d.tipo || edicao.observacao !== d.observacao
    || edicao.intervalo_envio_min !== (d.intervalo_envio_min ? String(d.intervalo_envio_min) : "")
    || edicao.alarme_sem_sinal_min !== (d.alarme_sem_sinal_min ? String(d.alarme_sem_sinal_min) : ""));
  const erroMotivoRealoc = realocacao && motivoRealocTocado && realocacao.motivo.trim().length < 5 ? "Escreva o motivo (5 letras ou mais); fica no histórico." : null;
  return (
    <section className="tl-pagina admin-estrutura">
      <CabecalhoTela voltar={voltar} migalhas={migalhas} titulo={<span className="tl-mono">{d.device_id}</span>}
        subtitulo={`${d.empresa} · ${d.tipo || "tipo não informado"}`}
        selos={<span className="linha-titulo selo-cabecalho"><span className={`nx-selo ${classe}`}>{rotulo}</span></span>}
        acoes={edita && !edicao && !realocacao ? <>
          <button type="button" className="nx-botao" onClick={() => setEdicao({ tipo: d.tipo, observacao: d.observacao,
            intervalo_envio_min: d.intervalo_envio_min ? String(d.intervalo_envio_min) : "",
            alarme_sem_sinal_min: d.alarme_sem_sinal_min ? String(d.alarme_sem_sinal_min) : "" })}>Editar</button>
          <button type="button" className="nx-botao" onClick={() => { setMotivoRealocTocado(false); setRealocacao({ filial: d.filial, municipio: d.municipio, unidade: d.unidade, motivo: "" }); }}>Realocar</button>
        </> : undefined} />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      <div className="estrutura-grade">
        <div className="estrutura-principal">
          <section className="tl-bloco admin-secao" aria-label="Dados do cadastro">
            <header><h3>Cadastro</h3></header>
            <dl className="ficha-usuario ficha-dados">
              <dt>Operadora</dt><dd>{d.empresa}</dd>
              <dt>Tópico MQTT</dt><dd className="tl-mono">{d.topico}/…</dd>
              <dt>Tipo</dt><dd>{d.tipo || "—"}</dd>
              <dt>Observação</dt><dd>{d.observacao || "—"}</dd>
              <dt>Envia a cada</dt><dd>{d.intervalo_envio_min ? duracao(d.intervalo_envio_min) : <span className="nx-suave">não informado</span>}
                {d.ritmo_medido && <small className="nx-suave"> · medido: {duracao(d.ritmo_medido.mediana_min)} (7 dias)</small>}</dd>
              <dt>Alarmar sem sinal após</dt><dd>{duracao(d.alarme_sem_sinal_efetivo_min)}{d.alarme_sem_sinal_min ? "" : <small className="nx-suave"> (padrão)</small>}</dd>
              <dt>Ativo da rede</dt><dd>{d.ativos.length ? d.ativos.map((a) => <Link key={a.id} to={`/cadastro/${a.id}`}>{a.identificacao} </Link>)
                : <span className="nx-suave">nenhum (ligue pelo campo "Dispositivo de telemetria" do ativo)</span>}</dd>
              {d.revogado_em && <><dt>Revogado</dt><dd>{quando(d.revogado_em)} por {d.revogado_por}: {d.motivo_revogacao}</dd></>}
              {d.desativado_em && <><dt>Desativado</dt><dd>{quando(d.desativado_em)} por {d.desativado_por}: {d.motivo_desativacao}</dd></>}
              <dt>Cadastrado</dt><dd>{d.criado_em ? quando(d.criado_em) : "—"}{d.criado_por ? ` por ${d.criado_por}` : ""}</dd>
            </dl>
          </section>
          {edita && edicao && (
            <form className="tl-bloco" aria-label="Dados do dispositivo" onSubmit={(e) => { e.preventDefault(); salvarEdicao(); }}>
              <div className="tl-painel-aba">
                <GrupoCampos titulo="Dados do dispositivo">
                  <Campo id="disp-tipo" rotulo="Tipo de equipamento">
                    <input id="disp-tipo" list="tipos-dispositivo" value={edicao.tipo} onChange={(e) => setEdicao({ ...edicao, tipo: e.target.value.trim().toLowerCase() })} />
                  </Campo>
                  <datalist id="tipos-dispositivo">{TIPOS.map((t) => <option key={t} value={t} />)}</datalist>
                  <Campo id="disp-obs" rotulo="Observação">
                    <input id="disp-obs" maxLength={300} value={edicao.observacao} onChange={(e) => setEdicao({ ...edicao, observacao: e.target.value })} />
                  </Campo>
                </GrupoCampos>
                <GrupoCampos titulo="Comunicação">
                  <CamposRitmo valor={edicao} aoMudar={(v) => setEdicao({ ...edicao, ...v })} medido={d.ritmo_medido} />
                </GrupoCampos>
              </div>
              <RodapeFormulario sujo={edicaoSuja} valido={!errosEdicao?.intervalo_envio_min && !errosEdicao?.alarme_sem_sinal_min}
                aoCancelar={() => setEdicao(null)} aoSalvar={salvarEdicao} />
            </form>
          )}
          {edita && realocacao && (
            <form className="tl-bloco" aria-label="Realocação" onSubmit={(e) => { e.preventDefault(); void executar(() => api(`${base}/realocacao`, { corpo: { versao: d.versao, ...realocacao } }), "Dispositivo realocado: o tópico novo vale na próxima recarga da ingestão (até 1 min)."); }}>
              <div className="tl-painel-aba">
                <GrupoCampos titulo="Realocar o dispositivo">
                  <CamposLocalizacao estrutura={estrutura} valor={realocacao} aoMudar={(v) => setRealocacao({ ...realocacao, ...v })} />
                  <Campo id="disp-motivo-realoc" rotulo="Motivo da realocação" erro={erroMotivoRealoc} largo
                    ajuda="O tópico MQTT muda; o motivo fica no histórico do cadastro.">
                    <input id="disp-motivo-realoc" required minLength={5} maxLength={300} value={realocacao.motivo} onBlur={() => setMotivoRealocTocado(true)}
                      onChange={(e) => setRealocacao({ ...realocacao, motivo: e.target.value })} />
                  </Campo>
                </GrupoCampos>
              </div>
              <div className="tl-rodape">
                <span className="tl-estado-form">O CLP passa a publicar no tópico novo.</span>
                <button type="button" className="tl-fantasma" onClick={() => setRealocacao(null)}>Cancelar</button>
                <button type="submit" className="nx-botao nx-botao-principal">Realocar</button>
              </div>
            </form>
          )}
          {edita && d.situacao !== "revogado" && !edicao && !realocacao && (
            <section className="tl-bloco admin-secao" aria-label="Situação do dispositivo">
              <header><h3>Situação</h3><span className="tl-subtitulo">{d.situacao === "ativo" ? "a ingestão aceita as leituras" : "a ingestão recusa as leituras"}</span></header>
              <div className="admin-corpo">
                <div className="form-linha acoes-conta">
                  {d.situacao === "ativo" ? <>
                    <Campo id="disp-motivo" rotulo="Motivo da desativação" ajuda="5 letras ou mais; fica no histórico">
                      <input id="disp-motivo" value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} />
                    </Campo>
                    <div className="tl-campo"><button type="button" className="nx-botao" disabled={motivo.trim().length < 5}
                      onClick={() => void executar(() => api(`${base}/desativar`, { corpo: { motivo } }), "Dispositivo desativado: a ingestão passa a recusar.")}>Desativar</button></div>
                  </> : <div className="tl-campo"><button type="button" className="nx-botao" onClick={() => void executar(() => api(`${base}/reativar`, { corpo: {} }), "Dispositivo reativado.")}>Reativar</button></div>}
                </div>
              </div>
            </section>
          )}
          {d.pode_revogar && pode.revogarDispositivo(usuario) && <p className="nx-suave" style={{ margin: 0 }}>Revogar ou reativar um dispositivo comprometido: <Link to="/cco/seguranca">CCO › Segurança</Link>.</p>}
          <Certificados deviceId={d.device_id} ativo={d.situacao === "ativo"} />
        </div>
        <aside className="estrutura-lado">
          <section className="tl-bloco admin-secao" aria-label="Eventos de segurança">
            <header><h3>Eventos de segurança</h3></header>
            <div className="admin-corpo">
              <ul className="auditoria tl-linha-tempo">
                {d.eventos_seguranca.map((e, i) => <li key={i}><b>{e.tipo}</b><small>{quando(e.instante)} · {e.severidade}{e.motivo ? ` · ${e.motivo}` : ""}</small></li>)}
                {!d.eventos_seguranca.length && <li className="nx-suave sem-eventos">sem eventos</li>}
              </ul>
            </div>
          </section>
          {daEstrutura && (
            <section className="tl-bloco admin-secao" aria-label="Histórico do cadastro">
              <header><h3>Histórico do cadastro</h3><span className="tl-subtitulo">nada se apaga</span></header>
              <div className="admin-corpo">
                <ul className="auditoria tl-linha-tempo">
                  {d.historico.map((e, i) => (
                    <li key={i}><b>{EVENTO[e.evento] ?? e.evento}</b><small>{quando(e.instante)} · por {e.autor}
                      {Object.entries(e.detalhe).filter(([k]) => k !== "dispositivo").map(([k, v]) => ` · ${k}: ${Array.isArray(v) ? v.join(" → ") : String(v)}`).join("")}</small></li>
                  ))}
                  {!d.historico.length && <li className="nx-suave sem-eventos">sem eventos</li>}
                </ul>
              </div>
            </section>
          )}
        </aside>
      </div>
    </section>
  );
}
